import { HTMLAttributes, RefObject } from "react";

import { useVirtualizer, VirtualItem, Virtualizer } from "@tanstack/react-virtual";

const VIRTUALIZATION_THRESHOLD: number = 100;
const ESTIMATED_ROW_HEIGHT: number = 37;
const VIRTUAL_ROW_OVERSCAN: number = 10;
const HEADER_ARIA_ROW_INDEX: number = 1;
const FIRST_BODY_ARIA_ROW_INDEX: number = 2;

interface IdentifiedRow {
  id: string;
}

interface VirtualRowProps {
  "data-index"?: number;
  "aria-rowindex"?: number;
  ref?: (element: Element | null) => void;
}

interface VisibleRow<Row> {
  row: Row;
  rowProps: VirtualRowProps;
}

interface ThresholdRowVirtualizerRequest<Row> {
  rowList: readonly Row[];
  totalCount: number;
  scrollElementRef: RefObject<HTMLDivElement>;
}

interface ThresholdRowVirtualizerResult<Row> {
  tableProps: HTMLAttributes<HTMLTableElement>;
  headerRowProps: HTMLAttributes<HTMLTableRowElement>;
  visibleRowList: VisibleRow<Row>[];
  topSpacerHeight: number;
  bottomSpacerHeight: number;
}

export function useThresholdRowVirtualizer<Row extends IdentifiedRow>({
  rowList,
  totalCount,
  scrollElementRef,
}: ThresholdRowVirtualizerRequest<Row>): ThresholdRowVirtualizerResult<Row> {
  const isVirtualized: boolean = rowList.length > VIRTUALIZATION_THRESHOLD;
  const virtualizer: Virtualizer<HTMLDivElement, Element> = useVirtualizer({
    count: isVirtualized ? rowList.length : 0,
    getScrollElement: (): HTMLDivElement | null => scrollElementRef.current,
    estimateSize: (): number => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index: number): string | number => rowList[index]?.id ?? index,
    overscan: VIRTUAL_ROW_OVERSCAN,
  });
  const itemList: VirtualItem[] = isVirtualized ? virtualizer.getVirtualItems() : [];
  const visibleRowList: VisibleRow<Row>[] = isVirtualized
    ? itemList.map(
        (item: VirtualItem): VisibleRow<Row> => ({
          row: rowList[item.index],
          rowProps: {
            "data-index": item.index,
            "aria-rowindex": item.index + FIRST_BODY_ARIA_ROW_INDEX,
            ref: virtualizer.measureElement,
          },
        }),
      )
    : rowList.map((row: Row): VisibleRow<Row> => ({ row, rowProps: {} }));
  return {
    tableProps: isVirtualized ? { "aria-rowcount": totalCount + HEADER_ARIA_ROW_INDEX } : {},
    headerRowProps: isVirtualized ? { "aria-rowindex": HEADER_ARIA_ROW_INDEX } : {},
    visibleRowList,
    topSpacerHeight: itemList[0]?.start ?? 0,
    bottomSpacerHeight: isVirtualized ? virtualizer.getTotalSize() - (itemList.at(-1)?.end ?? 0) : 0,
  };
}
